import React, { useState } from "react";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import firebaseStorageService from "@/services/firebaseStorageService";
import { showError, showInfo } from "@/store/errorStore";
import { useAuth } from "@/hooks/useAuth";

/**
 * Componente de Upload de Imagem para Firebase Storage
 *
 * @param {string} value - URL atual da imagem
 * @param {function} onChange - Callback com a nova URL
 * @param {string} folder - Pasta no storage ('products', 'categories', 'banners')
 * @param {string} itemId - ID do item (produto, categoria, etc)
 * @param {string} companyCode - Empresa dona do arquivo (padrão: empresa da sessão).
 *   Todo arquivo fica em {companyCode}/{folder}/... para não misturar empresas.
 * @param {number} maxSizeMB - Tamanho máximo em MB (padrão: 5)
 * @param {string} placeholder - Texto do placeholder
 */
export function FirebaseImageUpload({
  value,
  onChange,
  folder = "products",
  itemId = "",
  companyCode,
  maxSizeMB = 5,
  placeholder = "Clique ou arraste uma imagem",
}) {
  const auth = useAuth();
  const ownerCode = companyCode || auth.companyCode || "sem-empresa";
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(value || null);
  const [dragActive, setDragActive] = useState(false);

  const handleFileSelect = async (file) => {
    if (!file) return;

    try {
      // Validar arquivo
      firebaseStorageService.validateFile(file, maxSizeMB);

      // Preview local
      const localPreview = URL.createObjectURL(file);
      setPreviewUrl(localPreview);
      setUploading(true);
      setProgress(0);

      // Upload para Firebase, sempre dentro da pasta da empresa
      const prefix = itemId ? `${itemId}_` : folder === "banners" ? "banner_" : "";
      const downloadURL = await firebaseStorageService.uploadFileWithProgress(
        file,
        `${ownerCode}/${folder}/${prefix}${Date.now()}_${file.name}`,
        setProgress
      );

      // Limpar preview local
      URL.revokeObjectURL(localPreview);

      // Atualizar com URL do Firebase
      setPreviewUrl(downloadURL);
      onChange(downloadURL);

      showInfo("Imagem enviada com sucesso!", "Upload Concluído");
    } catch (error) {
      console.error("Erro no upload:", error);
      showError(error.message, "Erro no Upload");
      setPreviewUrl(value || null);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer?.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemove = async () => {
    if (previewUrl && previewUrl.includes("firebasestorage")) {
      try {
        await firebaseStorageService.deleteFile(previewUrl);
        showInfo("Imagem removida com sucesso", "Removida");
      } catch (error) {
        console.error("Erro ao remover:", error);
      }
    }

    setPreviewUrl(null);
    onChange("");
  };

  return (
    <div className="space-y-3">
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-4 transition-all
          ${dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300"}
          ${
            uploading
              ? "opacity-50 cursor-not-allowed"
              : "cursor-pointer hover:border-gray-400"
          }
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {previewUrl ? (
          <div className="relative">
            <img
              src={previewUrl}
              alt="Preview"
              className="w-full h-48 object-cover rounded-lg"
            />
            {!uploading && (
              <button
                onClick={handleRemove}
                className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
                type="button"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            {uploading && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                <div className="text-white text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                  <p className="text-sm font-semibold">
                    Enviando... {progress}%
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center h-48 cursor-pointer">
            <ImageIcon className="h-12 w-12 text-gray-400 mb-3" />
            <p className="text-sm text-gray-600 mb-1">{placeholder}</p>
            <p className="text-xs text-gray-400">
              JPG, PNG, GIF ou WEBP (máx. {maxSizeMB}MB)
            </p>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileInput}
              disabled={uploading}
              className="hidden"
            />
          </label>
        )}
      </div>

      {uploading && (
        <div className="space-y-2">
          <Progress value={progress} className="h-2" />
          <p className="text-xs text-gray-500 text-center">
            Enviando para Firebase Storage...
          </p>
        </div>
      )}

      {!previewUrl && !uploading && (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              document.querySelector(`input[type="file"]`)?.click()
            }
            className="w-full"
          >
            <Upload className="h-4 w-4 mr-2" />
            Selecionar Imagem
          </Button>
        </div>
      )}
    </div>
  );
}

export default FirebaseImageUpload;
